import { useQuery } from "@apollo/client"
import moment from "moment"
import { useContext, useState } from "react"
import { Table } from "reactstrap"
import { AppContext } from "../../context"
import { ADMIN_ROUTE, ROLES, TEMP_BILLING_COST_RATES } from "../../utils/constant"
import GoBack from "../GoBack"
import Loader from "../layout/Loader"
import { ITempBillingCostRate, TEMP_BILLING_COST_RATES as TEMP_BILLING_COST_RATES_QUERY } from "../temp-billing-reconciliation/gql"
import AddRatePeriodModal from "./AddRatePeriodModal"

// The People 2.0 cost-rate periods used to recompute what People 2.0 owes us on
// each weekly-activity line ("Owed to RealREPP" on the reconciliation page):
// their fee, charged on the full invoice amount, and the employee burden,
// charged on the gross wage at the California or the non-California rate.
//
// A period applies to weeks ENDING on or after its effective-from date, until
// the next period starts. Periods are append-only — there is no edit or delete —
// because a settled invoice froze the figure its period produced, and history
// must not be rewritten underneath it. A rate change is a new period.

/** Stored 'YYYY-MM-DD'; shown the way the People 2.0 reports write dates. */
const dateText = (value?: string | null): string =>
  value ? moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY') : '--'

/** Rates as entered: 5 -> "5%", 15.31 -> "15.31%". */
const pctText = (value?: number | null): string =>
  typeof value === 'number' && isFinite(value) ? `${Number(value.toFixed(4))}%` : '--'

const addedBy = (rate: ITempBillingCostRate): string => {
  // No user on a period means the migration seeded it from the contract.
  if (!rate?.createdBy) return 'Initial setup'
  const name = `${rate?.createdBy?.firstName || ''} ${rate?.createdBy?.lastName || ''}`.trim()
  return name || '--'
}

const TempBillingCostRatesList = () => {
  const { userRoles } = useContext(AppContext)
  const isExecutive = !!userRoles?.includes(ROLES.EXECUTIVE)
  const [addOpen, setAddOpen] = useState<boolean>(false)

  const { data, loading, error, refetch } = useQuery<{
    tempBillingCostRates: { rates: Array<ITempBillingCostRate>, cutoff?: string | null }
  }>(TEMP_BILLING_COST_RATES_QUERY, {
    skip: !isExecutive,
    fetchPolicy: 'network-only',
  })

  // Client-side gate is convenience only — the server guards every operation.
  if (!isExecutive) return null

  const rates = data?.tempBillingCostRates?.rates || []
  const cutoff = data?.tempBillingCostRates?.cutoff || '2026-01-01'
  // The newest period's effective-from: a new one must start after it.
  const latest = rates.reduce<string>(
    (max, rate) => (rate?.effectiveFrom > max ? rate?.effectiveFrom : max),
    '',
  )

  return (
    <div className="admin-tabs team-tabs">
      <GoBack url={ADMIN_ROUTE} />

      <div className="primaryHeaderSpacing priamryHeading d-flex justify-content-between align-items-center">
        <h5 className="m-0">{TEMP_BILLING_COST_RATES}</h5>
        <button
          type="button"
          className="buttonGenericStyle filledButton"
          onClick={() => setAddOpen(true)}
        >
          Add period
        </button>
      </div>

      <p className="text-muted">
        Rates used to recompute recruiter profit on the weekly activity report. Calculated for
        weeks ending on or after {moment(cutoff, 'YYYY-MM-DD').format('M/D/YYYY')}. Periods are
        append-only; add a new period when People 2.0 changes a rate.
      </p>

      {loading && <Loader loaderClass="sm-height" />}

      {!loading && error && (
        <p className="text-danger">{error?.message || 'Unable to load the cost rates'}</p>
      )}

      {!loading && !error && (
        <div className="table-responsive">
          <Table>
            <thead>
              <tr>
                <th>Effective from</th>
                <th className="text-right">People 2.0 fee %</th>
                <th className="text-right">Burden CA %</th>
                <th className="text-right">Burden non-CA %</th>
                <th>Note</th>
                <th>Added by</th>
                <th>Added on</th>
              </tr>
            </thead>
            <tbody>
              {!rates.length && (
                <tr>
                  <td colSpan={7} className="text-muted text-center">
                    No rate periods yet — nothing can be calculated until one is added.
                  </td>
                </tr>
              )}
              {rates.map((rate) => (
                <tr key={rate?.id}>
                  <td>{dateText(rate?.effectiveFrom)}</td>
                  <td className="text-right">{pctText(rate?.people20FeePct)}</td>
                  <td className="text-right">{pctText(rate?.burdenCaPct)}</td>
                  <td className="text-right">{pctText(rate?.burdenNonCaPct)}</td>
                  <td style={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>{rate?.note || '--'}</td>
                  <td>{addedBy(rate)}</td>
                  <td>{rate?.createdAt ? moment(rate?.createdAt).format('MM/DD/YYYY') : '--'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <AddRatePeriodModal
        isOpen={addOpen}
        toggle={() => setAddOpen(false)}
        minDate={cutoff}
        latestEffectiveFrom={latest}
        refetch={refetch}
      />
    </div>
  )
}

export default TempBillingCostRatesList
